import { useNavigate } from 'react-router-dom';
import { useBasePath, focusHref } from '../../../../lib/config/nav-helpers';
import { DataCard, StatusBadge, ScoreRing, SkeletonList, QueryError, UnlockHint } from '../../../../components/ui';
import { ScopeBadge } from '../../../../components/ScopeBadge';
import type { FollowUpItem } from '../../../../lib/follow-up';

export interface FollowUpsWidgetProps {
  followUps: { data?: any; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
}

/** Where a follow-up row opens: the client page, else the deal on the pipeline, else the Actions inbox. */
export function followUpHref(basePath: string, item: Pick<FollowUpItem, 'id' | 'clientId' | 'dealId'>): string {
  if (item.clientId) return `${basePath}/clients/${item.clientId}${item.dealId ? `?deal=${encodeURIComponent(item.dealId)}` : ''}`;
  if (item.dealId) return focusHref(basePath, '/pipeline', item.dealId);
  return focusHref(basePath, '/actions', item.id);
}

export function FollowUpsWidget({ followUps }: FollowUpsWidgetProps) {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const items: FollowUpItem[] = followUps.data?.items ?? [];
  const count = items.length;

  return (
    <DataCard
      title="Follow-Up Queue"
      subtitle="Who is waiting on you, from email, calls, calendar and the CRM"
      badge={
        <div className="flex items-center gap-1.5">
          <ScopeBadge scope={followUps.data?.scope} />
          {count > 0 && <StatusBadge status="info" label={String(count)} size="xs" />}
        </div>
      }
    >
      {followUps.isLoading ? <SkeletonList rows={3} /> : followUps.isError ? <QueryError error={followUps.error} onRetry={() => followUps.refetch()} compact /> : (
        <div className="space-y-2">
          {items.slice(0, 5).map((item) => (
            <button type="button" key={item.id} onClick={() => navigate(followUpHref(basePath, item))} className="w-full text-left flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 hover:bg-surface-1 hover:border-border-strong transition-colors cursor-pointer">
              <ScoreRing score={item.urgencyScore} size={32} strokeWidth={3} colorScale="risk" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-ink">{item.contactName}{item.company && item.company !== item.contactName ? <span className="text-ink-dim font-normal"> · {item.company}</span> : null}</p>
                <p className="mt-0.5 truncate text-2xs text-ink-dim" title={item.detail ?? undefined}>{item.reason}</p>
              </div>
              <StatusBadge status={item.urgency === 'critical' || item.urgency === 'high' ? 'critical' : item.urgency === 'medium' ? 'warning' : 'neutral'} label={item.suggestedAction} size="xs" />
            </button>
          ))}
          {/* The API nulls the count unless every source feeding the queue is visible: then "none" is not claimable. */}
          {followUps.data && items.length === 0 && followUps.data.totalContacts === null && (
            <p className="text-2xs text-ink-dim italic">Nothing to show yet: Waypoint can't see all of your email, CRM and billing, so it can't say who is waiting on you.</p>
          )}
          {(!followUps.data || (items.length === 0 && followUps.data.totalContacts !== null)) && (
            <UnlockHint
              need="email"
              unlocks="and Waypoint surfaces who's waiting on a reply, ranked by urgency."
              connectedFallback="No follow-ups queued — inbox zero on commitments."
            />
          )}
        </div>
      )}
    </DataCard>
  );
}
